import { colors } from "@bg/shared/lib/theme";
import {
  Button,
  darken,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  makeStyles,
  TextField,
} from "@material-ui/core";
import { Star, StarBorder } from "@material-ui/icons";
import React, { FunctionComponent, useState } from "react";
import Rating from "react-rating";

const useStyles = makeStyles({
  container: {
    paddingTop: ".75em",
    paddingBottom: ".75em",
  },
  content: {
    textAlign: "center",
  },
  titleBar: {
    backgroundColor: colors.deepSeaBlue,
    opacity: 0.87,
    color: colors.white,
  },
  closeButton: {
    float: "right",
    color: colors.white,
  },
  feedbackInput: {
    paddingTop: "1rem",
    width: "35em",
  },
  cancelButton: {
    textTransform: "none",
    color: colors.white,
    backgroundColor: colors.mediumBrown,
    "&:hover": {
      background: darken(colors.mediumBrown, 0.1),
    },
  },
  ratingSpan: {
    paddingTop: "1rem",
  },
  saveButton: {
    textTransform: "none",
    color: colors.white,
    backgroundColor: colors.powderBlue,
    "&:hover": {
      background: darken(colors.powderBlue, 0.1),
    },
  },
  starColor: {
    color: colors.yellowGold,
  },
});

interface RateExperienceDialogProps {
  isOpen: boolean;
  closeDialog: () => void;
  saveFeedback: (rating: number, feedback: string) => void;
  title?: string;
}

const RateExperienceDialog: FunctionComponent<RateExperienceDialogProps> = ({
  title,
  isOpen,
  closeDialog,
  saveFeedback,
}) => {
  const classes = useStyles();

  const getRatingDescription = (rating: number): string => {
    switch (rating) {
      case 1:
        return "Very Poor";
      case 2:
        return "Poor";
      case 3:
        return "Fair";
      case 4:
        return "Good";
      case 5:
        return "Excellent";

      default:
        return "";
    }
  };

  const [rating, setRating] = useState(5);
  const [feedback, setFeedback] = useState("");
  const [ratingDescription, setRatingDescription] = useState(
    getRatingDescription(rating),
  );

  const handleRatingClick = (value: number) => {
    setRating(value);
  };

  const handleRatingHover = (value: number) => {
    if (value) {
      setRatingDescription(getRatingDescription(value));
    } else {
      setRatingDescription(getRatingDescription(rating));
    }
  };

  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFeedback(event.target.value);
  };

  const save = async () => {
    saveFeedback(rating, feedback);
    closeDialog();
  };

  return (
    <Dialog open={isOpen} maxWidth="lg">
      <DialogTitle id="form-dialog-title" className={classes.titleBar}>
        {title || "How Would You Rate Your Experience With This Order?"}
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Rating
          initialRating={rating}
          onChange={handleRatingClick}
          onHover={handleRatingHover}
          emptySymbol={<StarBorder fontSize="large" />}
          fullSymbol={<Star fontSize="large" className={classes.starColor} />}
          aria-label="ratingStars"
          className={classes.ratingSpan}
        />
        <br />
        {ratingDescription}
        <br />
        <TextField
          id="form-text-input"
          variant="outlined"
          aria-label="feedbackText"
          value={feedback}
          placeholder="Please provide additional feedback"
          onChange={handleTextChange}
          multiline={true}
          minRows={5}
          className={classes.feedbackInput}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={closeDialog} className={classes.cancelButton}>
          Cancel
        </Button>
        <Button onClick={save} className={classes.saveButton}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RateExperienceDialog;
